'use client';

import Member from '@/components/Product/Member';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { useStore } from '@/providers/app-store-provider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { formatPriceNumberWithSymbol, priceInfo } from '@/utils/price';
import { useTranslations } from '@/i18n/use-translations';
import Image from '@/components/Image';
import { DivaEyeglassesData } from '../types';

interface DivaSparklingProps {
  pageData: DivaEyeglassesData;
  deviceType: EPlatform;
}

export default function DivaSparkling({ pageData, deviceType }: DivaSparklingProps) {
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const tButtons = useTranslations('activity.divaEyeglasses.buttons');

  const { navigate } = useAppNavigation();
  const isWap = deviceType !== EPlatform.WEB;

  // 类型转换以访问product_data属性
  const data = pageData as any;

  // 安全检查
  if (!data.product_data?.list || data.product_data.list.length === 0) {
    return null;
  }

  const title = data.product_data.title || '';
  const productList = data.product_data.list || [];
  const moreText = data.product_data.more_text || '';
  const moreGroupId = data.product_data.more_group_id;

  // 获取商品展示图片
  const getImage = (item: any) => {
    if (item.image?.product && item.image.product.length > 0) {
      return item.image.product[0];
    }
    return item.main_img || '';
  };

  // 商品详情跳转
  const handleProductClick = (item: any) => {
    navigate(`/goods-detail/${item.shop_sku}`);
  };

  // VIEW MORE 跳转
  const handleViewMore = () => {
    if (moreGroupId) {
      // 设置进入页面场景标识（与Vue版本一致）
      try {
        sessionStorage.setItem('zl_goodsListEnterPageScene', '活动专题页');
      } catch {
        // 忽略sessionStorage设置失败
      }

      navigate(`/goods-list/${moreGroupId}`);
    }
  };

  if (isWap) {
    // WAP端布局 - 基于WAP版本Sparkling.vue
    return (
      <div className="diva-sparking">
        <h2
          className="title text-center pt-[36px] font-bb text-[28px] text-[#dcb883] leading-[30px]"
          dangerouslySetInnerHTML={{ __html: title }}
        />

        <ul className="flex justify-between flex-wrap px-[14px] mt-[20px]">
          {productList.map((item: any) => (
            <li key={item.shop_sku} className="diva-sparking-goods-item w-[168px] mb-[11px]">
              <a
                href={`/goods-detail/${item.shop_sku}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleProductClick(item);
                }}
                className="block w-full"
              >
                {/* 商品图片容器 - 与PC端结构一致 */}
                <div className="img-box relative bg-white rounded-t-[10px] p-[6px] pb-0 h-[130px] box-border">
                  <div className="img-wrap h-[124px] overflow-hidden flex items-center justify-center box-border rounded-t-[10px]">
                    <Image
                      src={getImage(item)}
                      alt={item.name}
                      width={140}
                      height={120}
                      className="w-full object-contain"
                    />
                  </div>

                  {/* 会员标签 - 使用新的会员标签组件 */}
                  {userInfo.is_member === 1 && userInfo.member_level > 0 && (
                    <div className="absolute top-[6px] right-[6px]">
                      <Member
                        level={userInfo.member_level}
                        productData={item}
                        className="!mr-0 !text-[10px] !leading-[14px] !px-[4px]"
                      />
                    </div>
                  )}

                  {/* 缺货遮罩 */}
                  {item.stock < 1 && (
                    <div className="absolute inset-0 bg-[rgba(255,255,255,0.5)] flex items-center justify-center rounded-t-[10px]">
                      <div className="w-[35px] h-[35px] rounded-full bg-[rgba(0,0,0,0.7)] flex flex-col items-center justify-center text-white text-[9px]">
                        <span className="mb-[1px]">Sold</span>
                        <span>Out</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* 商品信息容器 - 与PC端结构一致，独立的底部区域 */}
                <div className="bottom-wrap relative w-full h-[38px] bg-[#f2f2f2] rounded-b-[10px] box-border">
                  <p className="name font-medium text-[12px] text-[#282828] text-center pt-[6px] leading-[13px] truncate px-[4px]">
                    {item.short_name || item.name}
                  </p>
                  <div className="price flex items-center justify-center pt-[1px]">
                    <span className="sale-price font-semibold text-[12px] text-[#282828] notranslate">
                      {formatPriceNumberWithSymbol(
                        priceInfo(userInfo, item, currency).sell_price,
                        currency,
                      )}
                    </span>
                    {priceInfo(userInfo, item, currency).sell_price !==
                      priceInfo(userInfo, item, currency).price && (
                      <span className="o-price text-[12px] text-[#999999] line-through ml-[3px] notranslate">
                        {formatPriceNumberWithSymbol(
                          priceInfo(userInfo, item, currency).price,
                          currency,
                        )}
                      </span>
                    )}
                  </div>
                </div>
              </a>
            </li>
          ))}
        </ul>

        {/* VIEW MORE 按钮 */}
        {moreGroupId && (
          <a
            href={`/goods-list/${moreGroupId}`}
            onClick={(e) => {
              e.preventDefault();
              handleViewMore();
            }}
            className="w-[200px] h-[40px] border border-[#dcb883] mx-auto mt-[40px] mb-[30px] font-bb font-bold text-[12px] text-[#dcb883] leading-[30px] flex items-center justify-center"
          >
            VIEW MORE
          </a>
        )}

        {/* 底部描述文字 */}
        {moreText && (
          <p
            className="font-br text-[12px] text-[#dcb883] leading-[20px] w-[345px] pb-[38px] mx-auto text-center"
            dangerouslySetInnerHTML={{ __html: moreText }}
          />
        )}
      </div>
    );
  }

  // PC端布局 - 基于PC版本Sparkling.vue
  return (
    <div className="sparkling mt-[4.95vw]">
      <h3
        className="sparkling-title font-bb text-[2.5vw] text-[#dcb883] leading-[2.6vw] mx-auto text-center"
        dangerouslySetInnerHTML={{ __html: title }}
      />

      <ul className="grid grid-cols-4 gap-[1.04vw] w-[87.5vw] mx-auto mt-[3.125vw]">
        {productList.map((item: any) => (
          <li key={item.shop_sku} className="rounded-[1.04vw] cursor-pointer box-border">
            <a
              href={`/goods-detail/${item.shop_sku}`}
              onClick={(e) => {
                e.preventDefault();
                handleProductClick(item);
              }}
              className="block"
            >
              {/* 商品图片容器 */}
              <div className="img-box relative bg-white rounded-t-[1.04vw] px-[0.31vw] pt-[0.31vw] h-[16.51vw] box-border">
                <div className="img-wrap h-[16.51vw] overflow-hidden flex items-center justify-center box-border rounded-[1.04vw] rounded-b-0">
                  <Image
                    src={getImage(item)}
                    alt={item.name}
                    width={400}
                    height={317}
                    className="w-full object-contain"
                  />
                </div>

                {/* 会员标签 - 使用新的会员标签组件 */}
                {userInfo.is_member === 1 && userInfo.member_level > 0 && (
                  <div className="absolute top-[0.52vw] right-[0.63vw]">
                    <Member level={userInfo.member_level} productData={item} className="!mr-0" />
                  </div>
                )}

                {/* 缺货遮罩 */}
                {item.stock < 1 && (
                  <div className="absolute inset-0 bg-[rgba(255,255,255,0.5)] cursor-pointer flex items-center justify-center rounded-[1.04vw] rounded-b-0">
                    <div className="flex flex-col items-center justify-center w-[5vw] h-[5vw] rounded-full text-center bg-[rgba(0,0,0,0.7)]">
                      <span className="text-[0.73vw] text-white mb-[0.26vw]">Sold</span>
                      <span className="text-[0.73vw] text-white">Out</span>
                    </div>
                  </div>
                )}
              </div>

              {/* 商品信息容器 */}
              <div className="bottom-wrap relative w-full h-[4.58vw] bg-[#f2f2f2] rounded-b-[1.04vw] box-border">
                <p className="name font-bs text-[0.83vw] text-[#282828] text-center pt-[1.3vw]">
                  {item.short_name || item.name}
                </p>
                <p className="price flex items-center justify-center pt-[0.28vw]">
                  <span className="sale-price font-bs text-[16px] text-[#282828] notranslate">
                    {formatPriceNumberWithSymbol(
                      priceInfo(userInfo, item, currency).sell_price,
                      currency,
                    )}
                  </span>
                  {priceInfo(userInfo, item, currency).sell_price !==
                    priceInfo(userInfo, item, currency).price && (
                    <span className="o-price font-bs text-[0.83vw] text-[#999999] line-through pl-[0.31vw] notranslate">
                      {formatPriceNumberWithSymbol(
                        priceInfo(userInfo, item, currency).price,
                        currency,
                      )}
                    </span>
                  )}
                </p>
              </div>
            </a>
          </li>
        ))}
      </ul>

      {/* VIEW MORE 按钮 */}
      {moreGroupId && (
        <a
          href={`/goods-list/${moreGroupId}`}
          onClick={(e) => {
            e.preventDefault();
            handleViewMore();
          }}
          className="view-more flex mx-auto mt-[4.69vw] w-[20.83vw] h-[4.17vw] border-2 border-[#dcb883] justify-center items-center text-[1.25vw] text-[#dcb883] font-bb"
        >
          {tButtons('viewMore')}
        </a>
      )}

      {/* 底部描述文字 */}
      {moreText && (
        <p
          className="p-text w-[86.88vw] mx-auto mt-[3.54vw] pb-[5.94vw] font-br text-[0.94vw] text-[#ffd093] leading-[1.56vw] text-center"
          dangerouslySetInnerHTML={{ __html: moreText }}
        />
      )}
    </div>
  );
}
